import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, DatePicker, Empty, Icon, Input, Row, Select, Slider, Spin, Statistic, Table, Tag, message
} from 'antd';
import L from 'leaflet';
import { Circle, CircleMarker, Map as LeafletMap, Marker, Polyline, TileLayer, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import moment from 'moment';

import { fetchBeaconData } from './utils/passiveAssetService';
import { normalizeBeaconList } from './utils/passiveAssetNormalizers';
import { loadAssetSensorIndex } from './utils/passiveAssetResolver';
import {
  buildDetectionIntervals, fetchReaderTrack, classifyIntervalTowing, buildOccupancyClusters
} from './utils/passiveAssetMovement';
import { rssiToDistance, rssiConfidenceBand, CONFIDENCE_COLORS } from './utils/passiveAssetRssi';
import usePlayback from './utils/usePlayback';
import { useGeoFences } from './utils/geoFenceService';
import {
  assetLabel, formatTime, formatHm, makePlaybackPinIcon, makeNoGpsPinIcon,
  ESRI_TILE_URL, ESRI_TILE_ATTRIBUTION, renderGeoFencePolygons
} from './utils/passiveAssetUi';

const { RangePicker } = DatePicker;
const { Option } = Select;

const validCoord = (r) =>
  Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)) &&
  !(Math.abs(Number(r.latitude)) < 1e-6 && Math.abs(Number(r.longitude)) < 1e-6);

const DEFAULT_RANGE = () => [moment().subtract(24, 'hours'), moment()];
const GAP_MS = 10 * 60 * 1000;

function BleReaderTracker() {
  const [range, setRange] = useState(DEFAULT_RANGE());
  const [imeiInput, setImeiInput] = useState('');
  const [reads, setReads] = useState([]);
  const [readerPoints, setReaderPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [queriedImei, setQueriedImei] = useState('');
  const [clusterByScan, setClusterByScan] = useState(false);
  const [hideUnlinked, setHideUnlinked] = useState(false);

  const [assetIndex, setAssetIndex] = useState(null);
  const mapRef = useRef(null);
  const tableBodyRef = useRef(null);

  const fromMs = range && range[0] ? range[0].valueOf() : null;
  const toMs = range && range[1] ? range[1].valueOf() : null;

  const playbackPinIcon = useMemo(() => makePlaybackPinIcon(), []);
  const noGpsPinIcon = useMemo(() => makeNoGpsPinIcon(), []);
  const { geoFences } = useGeoFences();

  useEffect(() => {
    let cancelled = false;
    loadAssetSensorIndex().then((idx) => { if (!cancelled) setAssetIndex(idx); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const resolveTag = useCallback((id) => (assetIndex ? assetIndex.resolveTag(id) : null), [assetIndex]);
  const resolveReader = useCallback((id) => (assetIndex ? assetIndex.resolveReader(id) : null), [assetIndex]);

  const load = useCallback(async () => {
    if (!fromMs || !toMs || fromMs >= toMs) { message.warning('Pick a valid date range.'); return; }
    const imei = (imeiInput || '').trim();
    if (!imei) { message.warning('Enter a Reader IMEI.'); return; }

    setLoading(true);
    setError(null);
    setNotice(null);
    setReads([]);
    setReaderPoints([]);
    setQueriedImei(imei);

    try {
      const res = await fetchBeaconData({ from: fromMs, to: toMs, imei });
      if (res && res.success === false) { setError(res.error || 'Failed to fetch beacon data'); setLoading(false); return; }
      const normalized = normalizeBeaconList(res.data === undefined ? res : res.data);
      setReads(normalized);

      const info = resolveReader(imei);
      const assetId = info ? info.assetId : null;
      if (!assetId) {
        setNotice('This reader IMEI is not linked to an asset, so its GPS route cannot be fetched. Showing detections only.');
      } else {
        const tr = await fetchReaderTrack({ readerImei: imei, assetId, startTs: fromMs, endTs: toMs });
        setReaderPoints(tr.points || []);
        if (!tr.success) setNotice(tr.error || 'No GPS route returned for this reader in the period.');
      }

      if (normalized.length === 0) setNotice('No BLE detections by this reader in the selected period.');
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [fromMs, toMs, imeiInput, resolveReader]);

  // Optionally drop detections of BLE tags not linked to any org asset. Only
  // applied once the asset directory is loaded (else we can't tell linkage).
  const effectiveReads = useMemo(
    () => ((hideUnlinked && assetIndex) ? reads.filter((r) => !!resolveTag(r.assetIdentifier)) : reads),
    [reads, hideUnlinked, assetIndex, resolveTag]
  );
  const unlinkedHidden = reads.length - effectiveReads.length;

  const intervals = useMemo(
    () => buildDetectionIntervals(effectiveReads, toMs).intervals,
    [effectiveReads, toMs]
  );

  // Classify each interval as towed using the reader GPS.
  const classifiedIntervals = useMemo(
    () => intervals.map((itv) => ({ ...itv, towed: classifyIntervalTowing(itv, readerPoints).towed })),
    [intervals, readerPoints]
  );

  // Raw FOUND/LOST detection events that have a location — plotted on the map
  // with a signal-range circle and the detected BLE asset's label.
  const detectionCircles = useMemo(() => effectiveReads.filter(validCoord).map((r) => {
    const distance = rssiToDistance(r.rssi);
    return {
      id: r.id,
      lat: Number(r.latitude),
      lng: Number(r.longitude),
      rssi: r.rssi,
      distance,
      confidence: rssiConfidenceBand(distance),
      status: String(r.status || '').toUpperCase(),
      assetIdentifier: r.assetIdentifier,
      ts: r.eventTime
    };
  }), [effectiveReads]);

  // Full reader route with on-board occupancy at each GPS point.
  const trackPoints = useMemo(() => {
    const pts = (readerPoints || []).map((p, i) => {
      const detections = classifiedIntervals
        .filter((itv) => itv.startTs <= p.ts && p.ts <= itv.endTs)
        .map((itv) => ({ assetIdentifier: itv.assetIdentifier, towed: itv.towed === true, rssi: itv.startRead ? itv.startRead.rssi : null }))
        .sort((a, b) => (Number(b.towed) - Number(a.towed)) || (Number(b.rssi) - Number(a.rssi)));
      return {
        id: p.id || `p-${i}`, ts: p.ts, lat: p.lat, lng: p.lng, speed: p.speed, noGps: p.noGps === true,
        detections, count: detections.length, towedCount: detections.filter((d) => d.towed).length
      };
    });
    for (let i = 0; i < pts.length; i += 1) pts[i].jumpFromPrev = Boolean(pts[i - 1] && (pts[i].ts - pts[i - 1].ts) > GAP_MS);
    return pts;
  }, [readerPoints, classifiedIntervals]);

  const clusters = useMemo(
    () => (clusterByScan ? buildOccupancyClusters(effectiveReads, toMs).map((c) => ({ id: c.key, ts: c.ts, lat: c.lat, lng: c.lng, detections: c.detections, count: c.count })) : []),
    [clusterByScan, reads, toMs]
  );

  const usingClusters = clusterByScan && clusters.length > 0;
  const activePoints = usingClusters ? clusters : trackPoints;
  const pb = usePlayback(activePoints.length);
  const activePoint = activePoints.length ? activePoints[Math.max(0, Math.min(pb.index, activePoints.length - 1))] : null;

  // Route colored by load state (towing / detected / empty).
  const routeSegments = useMemo(() => {
    if (usingClusters) return [];
    const edgeState = (p) => ((p.towedCount || 0) > 0 ? 'towing' : (p.count || 0) > 0 ? 'detected' : 'empty');
    const segs = [];
    let run = null;
    for (let i = 1; i < trackPoints.length; i += 1) {
      const a = trackPoints[i - 1]; const b = trackPoints[i];
      if (b.jumpFromPrev) { if (run) { segs.push(run); run = null; } continue; }
      const st = edgeState(a);
      if (st === 'empty') { if (run) { segs.push(run); run = null; } continue; }
      if (run && run.state === st) run.positions.push([b.lat, b.lng]);
      else { if (run) segs.push(run); run = { state: st, positions: [[a.lat, a.lng], [b.lat, b.lng]] }; }
    }
    if (run) segs.push(run);
    return segs;
  }, [usingClusters, trackPoints]);

  const baseRoute = useMemo(() => {
    if (usingClusters) return [];
    const runs = [];
    let run = null;
    for (let i = 0; i < trackPoints.length; i += 1) {
      const p = trackPoints[i];
      if (i > 0 && p.jumpFromPrev) { if (run && run.length >= 2) runs.push(run); run = [[p.lat, p.lng]]; }
      else { if (!run) run = []; run.push([p.lat, p.lng]); }
    }
    if (run && run.length >= 2) runs.push(run);
    return runs;
  }, [usingClusters, trackPoints]);

  const playedPositions = useMemo(
    () => activePoints.slice(0, Math.max(0, pb.index) + 1).map((p) => [p.lat, p.lng]),
    [activePoints, pb.index]
  );

  useEffect(() => {
    if (!mapRef.current || activePoints.length === 0) return;
    const map = mapRef.current.leafletElement || mapRef.current;
    if (!map || typeof map.fitBounds !== 'function') return;
    if (activePoints.length === 1) { map.setView([activePoints[0].lat, activePoints[0].lng], 17); return; }
    map.fitBounds(L.latLngBounds(activePoints.map((p) => [p.lat, p.lng])), { padding: [32, 32], maxZoom: 18 });
  }, [activePoints]);

  const readerInfo = resolveReader(queriedImei);

  const summary = useMemo(() => {
    const assets = new Set();
    const towed = new Set();
    classifiedIntervals.forEach((i) => { assets.add(i.assetIdentifier); if (i.towed) towed.add(i.assetIdentifier); });
    const peak = trackPoints.reduce((m, p) => Math.max(m, p.count || 0), 0);
    return { assets: assets.size, towed: towed.size, peak };
  }, [classifiedIntervals, trackPoints]);

  // Detection intervals table (one row per asset FOUND→LOST).
  const intervalRows = useMemo(
    () => classifiedIntervals.slice().sort((a, b) => a.startTs - b.startTs),
    [classifiedIntervals]
  );

  // Raw FOUND/LOST event rows (newest first).
  const eventRows = useMemo(
    () => effectiveReads.slice().sort((a, b) => (b.eventTime || 0) - (a.eventTime || 0)),
    [effectiveReads]
  );

  const seekToTs = useCallback((ts) => {
    pb.setIsPlaying(false);
    let best = 0; let bestDiff = Infinity;
    activePoints.forEach((p, i) => { const d = Math.abs(p.ts - ts); if (d < bestDiff) { bestDiff = d; best = i; } });
    pb.setIndex(best);
  }, [activePoints, pb]);

  const renderIdentity = (rawId, info) => (
    <div>
      <div style={{ fontWeight: 600 }}>{(info && (info.displayName || info.assetCode)) || rawId || '-'}</div>
      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
        {info && info.ownerCode ? `Owner ${info.ownerCode} · ` : ''}{rawId || ''}
        {info && info.viaDeviceCode ? ` · via ${info.viaDeviceCode}` : ''}
      </div>
    </div>
  );

  const eventColumns = [
    { title: 'Event Time', key: 'time', width: 170, render: (_, r) => formatTime(r.eventTime), sorter: (a, b) => (a.eventTime || 0) - (b.eventTime || 0), defaultSortOrder: 'descend' },
    { title: 'Status', key: 'status', width: 90, render: (_, r) => {
      const s = String(r.status || '').toUpperCase();
      return s === 'FOUND' ? <Tag color="green">FOUND</Tag> : s === 'LOST' ? <Tag color="red">LOST</Tag> : <Tag>{r.status || '-'}</Tag>;
    } },
    { title: 'BLE Asset', key: 'asset', render: (_, r) => renderIdentity(r.assetIdentifier, resolveTag(r.assetIdentifier)) },
    { title: 'RSSI', key: 'rssi', width: 90, render: (_, r) => (r.rssi !== null && r.rssi !== undefined ? `${r.rssi} dBm` : '-') },
    { title: '~Range', key: 'range', width: 90, render: (_, r) => (r.rssi !== null && r.rssi !== undefined ? `${rssiToDistance(r.rssi).toFixed(0)} m` : '-') },
    { title: 'Zone', key: 'zone', render: (_, r) => (r.zone && r.zone !== '-' ? r.zone : '-') },
    { title: 'Lat / Lng', key: 'latlng', width: 170, render: (_, r) => (validCoord(r) ? `${Number(r.latitude).toFixed(5)}, ${Number(r.longitude).toFixed(5)}` : '-') }
  ];

  const intervalColumns = [
    { title: 'Asset', key: 'asset', render: (_, r) => assetLabel(r.assetIdentifier, resolveTag(r.assetIdentifier)) },
    { title: 'FOUND', key: 'found', render: (_, r) => formatTime(r.startTs) },
    { title: 'LOST', key: 'lost', render: (_, r) => (r.open ? <Tag>ongoing</Tag> : formatHm(r.endTs)) },
    { title: 'Duration', key: 'dur', width: 110, render: (_, r) => moment.duration(Math.max(0, r.endTs - r.startTs)).humanize() },
    { title: 'Status', key: 'towed', width: 110, render: (_, r) => (r.towed ? <Tag color="blue">towed</Tag> : <Tag color="orange">detected</Tag>) }
  ];

  return (
    <div className="ble-reader-tracker">
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="BLE Reader Tracker — what a reader did, and what it carried"
        description="Pick one reader (IMEI). We plot its GPS route over the period and label where it detected or towed BLE assets. Blue route = carrying ≥1 asset, grey = empty."
      />

      <Card title={<span><Icon type="filter" /> Filters</span>} style={{ marginBottom: 16 }}>
        <Row gutter={12} type="flex" align="bottom">
          <Col xs={24} sm={10} md={8}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Date Range</div>
            <RangePicker style={{ width: '100%' }} showTime value={range} onChange={(v) => setRange(v || DEFAULT_RANGE())} format="YYYY-MM-DD HH:mm" />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Reader IMEI</div>
            <Input placeholder="e.g. 866943088122167" value={imeiInput} onChange={(e) => setImeiInput(e.target.value)} allowClear onPressEnter={load} />
          </Col>
          <Col>
            <Button type="primary" icon="search" onClick={load} loading={loading}>Track Reader</Button>
          </Col>
        </Row>
      </Card>

      {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}
      {notice && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={notice} />}

      {queriedImei && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Reader" value={assetLabel(queriedImei, readerInfo)} valueStyle={{ fontSize: 15 }} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="GPS points" value={trackPoints.length} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Assets seen" value={summary.assets} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Towed" value={summary.towed} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        </Row>
      )}

      <Card
        size="small"
        style={{ marginBottom: 12 }}
        title={<span><Icon type="environment" /> Reader Route <span style={{ fontWeight: 400, fontSize: 12, color: '#888', marginLeft: 8 }}>{usingClusters ? 'Bubble = a scan; size = concurrent assets' : 'Blue = carrying ≥1 asset · grey = empty · dotted = GPS gap'}</span></span>}
      >
        <Spin spinning={loading}>
          <Row gutter={12} type="flex" align="middle" style={{ marginBottom: 10, rowGap: 8 }}>
            <Col>
              <Checkbox checked={clusterByScan} onChange={(e) => setClusterByScan(e.target.checked)}>Cluster by reader scan (concurrent assets)</Checkbox>
            </Col>
            <Col>
              <Checkbox checked={hideUnlinked} onChange={(e) => setHideUnlinked(e.target.checked)}>
                Hide unlinked BLE tags{unlinkedHidden > 0 ? ` (${unlinkedHidden} hidden)` : ''}
              </Checkbox>
            </Col>
            <Col>
              <Button.Group size="small">
                <Button icon="fast-backward" onClick={pb.reset} disabled={activePoints.length === 0}>Reset</Button>
                <Button icon="step-backward" onClick={pb.back} disabled={activePoints.length === 0 || pb.index <= 0}>Back</Button>
                <Button type={pb.isPlaying ? 'default' : 'primary'} icon={pb.isPlaying ? 'pause' : 'caret-right'} onClick={pb.toggle} disabled={activePoints.length <= 1}>{pb.isPlaying ? 'Pause' : 'Play'}</Button>
                <Button icon="step-forward" onClick={pb.step} disabled={activePoints.length === 0 || pb.index >= activePoints.length - 1}>Step</Button>
              </Button.Group>
            </Col>
            <Col>
              <span style={{ fontSize: 12, color: '#666', marginRight: 4 }}>Speed:</span>
              <Select size="small" value={pb.speed} onChange={pb.setSpeed} style={{ width: 80 }}>
                {[0.5, 1, 2, 4, 8].map((s) => <Option key={s} value={s}>{s}x</Option>)}
              </Select>
            </Col>
            {activePoint && (
              <Col flex="auto">
                <span style={{ fontSize: 12, color: '#444' }}>
                  <strong>Reader {assetLabel(queriedImei, readerInfo)}</strong> · {formatTime(activePoint.ts)}
                  {!usingClusters && activePoint.speed != null ? ` · ${Number(activePoint.speed).toFixed(0)} km/h` : ''} ·{' '}
                  {usingClusters ? (
                    <Tag color="blue">{activePoint.count} on board</Tag>
                  ) : activePoint.count ? (
                    <>
                      <Tag color="blue">{activePoint.towedCount} towed</Tag>
                      <Tag color="orange">{activePoint.count - activePoint.towedCount} detected</Tag>
                    </>
                  ) : <Tag>empty</Tag>}
                  <span style={{ color: '#888' }}>
                    {(activePoint.detections || []).slice(0, 4).map((d) => assetLabel(d.assetIdentifier, resolveTag(d.assetIdentifier))).join(', ')}
                    {(activePoint.detections || []).length > 4 ? ` +${activePoint.detections.length - 4} more` : ''}
                  </span>
                </span>
              </Col>
            )}
          </Row>

          {activePoints.length > 1 && (
            <div style={{ marginBottom: 10 }}>
              <Slider
                min={0}
                max={activePoints.length - 1}
                value={pb.index}
                onChange={(v) => { pb.setIsPlaying(false); pb.setIndex(v); }}
                tipFormatter={(v) => (activePoints[v] ? formatHm(activePoints[v].ts) : '')}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                <span>{formatTime(activePoints[0].ts)}</span>
                <span>{pb.index + 1} / {activePoints.length}</span>
                <span>{formatTime(activePoints[activePoints.length - 1].ts)}</span>
              </div>
            </div>
          )}

          {activePoints.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={queriedImei ? 'No route/detections to map. The reader may be unlinked or have no GPS in this period.' : 'Enter a Reader IMEI and click Track Reader.'} />
          ) : (
            <div style={{ height: 520, border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
              <LeafletMap ref={mapRef} center={[activePoints[0].lat, activePoints[0].lng]} zoom={15} maxZoom={20} style={{ height: '100%', width: '100%' }}>
                <TileLayer attribution={ESRI_TILE_ATTRIBUTION} url={ESRI_TILE_URL} maxZoom={20} />
                {renderGeoFencePolygons(geoFences)}
                {usingClusters ? (
                  <>
                    {clusters.length >= 2 && <Polyline positions={clusters.map((c) => [c.lat, c.lng])} color="#bfbfbf" weight={1} opacity={0.5} dashArray="4,6" />}
                    {clusters.map((c, i) => (
                      <CircleMarker key={`c-${c.id}`} center={[c.lat, c.lng]} radius={5 + Math.min(18, c.count * 2)} color={i === pb.index ? '#722ed1' : '#1890ff'} fillColor={i === pb.index ? '#722ed1' : '#1890ff'} fillOpacity={i === pb.index ? 0.45 : 0.2} weight={i === pb.index ? 2 : 1} onClick={() => { pb.setIsPlaying(false); pb.setIndex(i); }}>
                        <LeafletTooltip direction="top"><span style={{ fontSize: 11 }}><strong>{c.count} on board</strong> @ {formatHm(c.ts)}</span></LeafletTooltip>
                      </CircleMarker>
                    ))}
                  </>
                ) : (
                  <>
                    {baseRoute.map((run, i) => <Polyline key={`base-${i}`} positions={run} color="#8c8c8c" weight={2} opacity={0.5} />)}
                    {routeSegments.map((seg, i) => <Polyline key={`seg-${i}`} positions={seg.positions} color={seg.state === 'towing' ? '#1890ff' : '#faad14'} weight={4} opacity={0.75} />)}
                    {pb.index > 0 && <Polyline positions={playedPositions} color="#722ed1" weight={3} opacity={0.85} />}

                    {/* Raw FOUND/LOST detection events: signal-range circle + labelled dot. */}
                    {detectionCircles.map((c, i) => {
                      const color = CONFIDENCE_COLORS[c.confidence] || '#1890ff';
                      return (
                        <Circle key={`dc-${c.id || i}`} center={[c.lat, c.lng]} radius={c.distance} color={color} fillColor={color} fillOpacity={0.08} weight={1} opacity={0.45}>
                          <LeafletTooltip direction="top">
                            <span style={{ fontSize: 11 }}>
                              {c.status === 'FOUND' ? 'FOUND' : c.status === 'LOST' ? 'LOST' : c.status} · {formatHm(c.ts)}<br />
                              {assetLabel(c.assetIdentifier, resolveTag(c.assetIdentifier))}<br />
                              {c.rssi} dBm · ~{c.distance.toFixed(0)} m
                            </span>
                          </LeafletTooltip>
                        </Circle>
                      );
                    })}
                    {detectionCircles.map((c, i) => (
                      <CircleMarker key={`de-${c.id || i}`} center={[c.lat, c.lng]} radius={3} weight={1}
                        color={c.status === 'FOUND' ? '#52c41a' : c.status === 'LOST' ? '#ff4d4f' : '#8c8c8c'}
                        fillColor={c.status === 'FOUND' ? '#52c41a' : c.status === 'LOST' ? '#ff4d4f' : '#8c8c8c'} fillOpacity={1}>
                        <LeafletTooltip direction="top">
                          <span style={{ fontSize: 11 }}>{assetLabel(c.assetIdentifier, resolveTag(c.assetIdentifier))} · {c.status}</span>
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}
                  </>
                )}
                {activePoint && (
                  <Marker position={[activePoint.lat, activePoint.lng]} icon={activePoint.noGps ? noGpsPinIcon : playbackPinIcon}>
                    <LeafletTooltip permanent direction="top" offset={[0, -22]} opacity={0.95}>
                      <span style={{ fontSize: 11 }}>
                        <strong>Reader {assetLabel(queriedImei, readerInfo)}</strong><br />
                        {formatHm(activePoint.ts)}{!usingClusters && activePoint.speed != null ? ` · ${Number(activePoint.speed).toFixed(0)} km/h` : ''}
                        {' · '}<strong>{usingClusters ? `${activePoint.count} on board` : activePoint.count ? `${activePoint.towedCount} towed · ${activePoint.count - activePoint.towedCount} detected` : 'empty'}</strong>
                        {(activePoint.detections || []).length > 0 && <br />}
                        {(activePoint.detections || []).slice(0, 8).map((d, di) => (
                          <span key={di} style={{ display: 'block', color: d.towed ? '#1890ff' : '#fa8c16' }}>
                            {d.towed ? '➜ ' : '• '}{assetLabel(d.assetIdentifier, resolveTag(d.assetIdentifier))}{d.towed ? '' : ' · detected'}
                          </span>
                        ))}
                      </span>
                    </LeafletTooltip>
                  </Marker>
                )}
              </LeafletMap>
            </div>
          )}
        </Spin>
      </Card>

      {eventRows.length > 0 && (
        <Card size="small" style={{ marginBottom: 12 }} title={<span><Icon type="profile" /> BLE Events ({eventRows.length})</span>}>
          <Table
            rowKey={(r, i) => `evt-${i}`}
            size="small"
            columns={eventColumns}
            dataSource={eventRows}
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: 1000 }}
            onRow={(record) => ({ onClick: () => seekToTs(record.eventTime), style: { cursor: 'pointer' } })}
          />
        </Card>
      )}

      {intervalRows.length > 0 && (
        <Card size="small" title={<span><Icon type="ordered-list" /> Detection windows ({intervalRows.length})</span>}>
          <div ref={tableBodyRef}>
            <Table
              rowKey={(r, i) => String(i)}
              size="small"
              columns={intervalColumns}
              dataSource={intervalRows}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              onRow={(record) => ({ onClick: () => seekToTs(record.startTs), style: { cursor: 'pointer' } })}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

export default BleReaderTracker;
